import React, { useState, useEffect, useCallback } from "react";
import Styles from "./TraderEntity.module.css";
import {
  createTraderMap,
  updateTraderMap,
  deleteTraderMap,
  getTraderMap,
} from "../../../../Services/Admin/Alter";
import {
  getAllCompanies,
  getAllConsumers,
  getAllGenerators,
} from "../../../../Services/GNA/GetData";
import { apiAssignCompanyTrader } from "../../../../Services/GNA/AlterApi";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import Fileupload from "../../../../commons/Inputs/FileUpload/Fileupload";
import MuiDateInput from "../../../../commons/Inputs/DateInput/MuiDateInput";
import { Modal } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

const entityTypeList = [
  { id: "CONSUMER", name: "Consumer" },
  { id: "GENERATOR", name: "Generator" },
];

const formatLocalDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Handles Jackson LocalDate serialized as [2026, 8, 20] array OR ISO string
const formatNocDate = (val) => {
  if (!val) return "Not Set";
  if (Array.isArray(val)) {
    const [y, m, d] = val;
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  if (typeof val === "string") {
    // already ISO format like "2026-08-20" or "2026-08-20T00:00:00"
    return val.substring(0, 10);
  }
  return String(val);
};

const emptyForm = {
  entityType: "CONSUMER",
  selectedEntity: null,
  docId: "",
  nocExpiryAt: null,
};

const TraderEntity = ({ companyId, userId, onViewDoc }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(entityTypeList[0]);
  const [formModal, setFormModal] = useState({ open: false, editData: null });
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);
  const [allConsumers, setAllConsumers] = useState([]);
  const [allGenerators, setAllGenerators] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const fetchAllEntities = async () => {
    setLoadingEntities(true);
    try {
      const companiesRes = await getAllCompanies();
      const companyList = companiesRes?.data?.data ?? [];
      const companyIds = companyList.map((company) => company.companyId);

      const [consumersRes, generatorsRes] = await Promise.all([
        getAllConsumers({ companyIds }),
        getAllGenerators({ companyIds }),
      ]);

      const rawConsumers = consumersRes?.data?.data?.page || [];
      const rawGenerators = generatorsRes?.data?.data?.page || [];
        setAllConsumers(rawConsumers.map((c) => ({
        id: c.consumerId,
        name: c.consumerName,
        companyId: c.companyId,
        status: c.accountStatus,
        isActive: c.isActive,
      })));

      setAllGenerators(rawGenerators.map((g) => ({
        id: g.generatorId,
        name: g.generatorName,
        companyId: g.companyId,
        status: g.accountStatus,
        isActive: g.isActive,
      })));


    } catch (err) {
      console.error("Failed to fetch consumers/generators", err);
    }
    setLoadingEntities(false);
  };

  useEffect(() => {
    fetchAllEntities();
  }, []);

        const fetchRecords = useCallback(async () => {
    setLoading(true);
    const res = await getTraderMap(companyId, filterType?.id);
    if (res?.data?.code === 200) {
      const allRecords = res?.data?.data || [];
      // Filter out entities that are inactive or not verified
            // Filter out entities that are inactive, unverified, or on-hold (only VERIFIED and active)
            // Filter out entities that are inactive (only keep active ones)
      

      // Only keep mappings belonging to active/verified entities
      const activeMappings = allRecords
      setRecords(activeMappings);
    } else {
      ErrorMessage(res?.data?.error || "Failed to fetch records");
    }
    setLoading(false);
  }, [companyId, filterType, allConsumers, allGenerators]);


  useEffect(() => {
    if (companyId) fetchRecords();
  }, [companyId, filterType, fetchRecords]);

  const openAddModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setFormModal({ open: true, editData: null });
  };

  const openEditModal = (record) => {
    const options = record.entityType === "CONSUMER" ? allConsumers : allGenerators;
    const selectedEnt = options.find(o => o.id === record.entityId) || {
      id: record.entityId,
      name: record.entityName,
      companyId: record.companyId
    };

    setFormData({
      entityType: record.entityType,
      selectedEntity: selectedEnt,
      docId: record.docId,
      nocExpiryAt: record.nocExpiryAt ? new Date(record.nocExpiryAt) : null,
    });
    setErrors({});
    setFormModal({ open: true, editData: record });
  };

  const closeFormModal = () => {
    setFormModal({ open: false, editData: null });
    setFormData(emptyForm);
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.entityType) newErrors.entityType = "Entity type is required";
    if (!formData.selectedEntity) newErrors.selectedEntity = "Company is required";
    if (!formData.docId) newErrors.docId = "Document is required";
    if (!formData.nocExpiryAt) newErrors.nocExpiryAt = "NOC Expiry date is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = ({ id }) => {
    setFormData((prev) => ({ ...prev, docId: id }));
    if (errors.docId) setErrors((prev) => ({ ...prev, docId: "" }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    let res;
    if (formModal.editData) {
      const payload = {
        id: formModal.editData.id,
        userId: Number(userId),
        companyId: formData.selectedEntity.companyId,
        entityType: formData.entityType,
        entityName: formData.selectedEntity.name,
        entityId: formData.selectedEntity.id,
        isShadowEntity: false,
        docId: formData.docId,
        nocExpiryAt: formData.nocExpiryAt ? formatLocalDate(formData.nocExpiryAt) : null,
      };
      res = await updateTraderMap(payload);
    } else {
      res = await apiAssignCompanyTrader({
        companyId: formData.selectedEntity.companyId,
        traderUserId: Number(userId),
        consumerId: formData.entityType === "CONSUMER" ? formData.selectedEntity.id : null,
        generatorId: formData.entityType === "GENERATOR" ? formData.selectedEntity.id : null,
        docId: formData.docId,
        expiryAt: formData.nocExpiryAt ? formatLocalDate(formData.nocExpiryAt) : null,
      });
    }

    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message || "Saved successfully");
      closeFormModal();
      fetchRecords();
    } else {
      ErrorMessage(res?.data?.error || "Failed to save");
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const res = await deleteTraderMap(deleteModal.id);
    if (res?.data?.code === 200) {
      SuccessMessage(res?.data?.message || "Deleted successfully");
      setDeleteModal({ open: false, id: null });
      fetchRecords();
    } else {
      ErrorMessage(res?.data?.error || "Failed to delete");
    }
    setDeleting(false);
  };

  const entityOptions = (formData.entityType === "CONSUMER" ? allConsumers : allGenerators)
  .filter((e) => e.isActive !== false);

  return (
    <div className={`display_flex_column_gap_8 ${Styles.container}`}>
      {/* Header */}
      <div className={Styles.header}>
        <div className={Styles.sectionTitle}>Trader Entity Mapping</div>
        <div className={Styles.headerRight}>
          <SelectInput
            list={entityTypeList}
            value={filterType}
            onChange={(val) => setFilterType(val)}
            example="Filter by Type"
            height={36}
          />
          <button className={Styles.addBtn} onClick={openAddModal}>
            <AddIcon style={{ fontSize: "16px" }} />
            Add
          </button>
        </div>
      </div>

      {/* Table */}
      <div className={Styles.tableWrapper}>
        {loading ? (
          <div className={Styles.loadingText}>Loading...</div>
        ) : records.length === 0 ? (
          <div className={Styles.emptyText}>No records found</div>
        ) : (
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Entity Name</th>
                <th>Entity Type</th>
                <th>Expiry Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => (
                <tr key={record.id}>
                  <td>{index + 1}</td>
                  <td>
                    {record.entityName ||
                      (record.entityType === "CONSUMER"
                        ? allConsumers.find((c) => c.id === record.entityId)?.name
                        : allGenerators.find((g) => g.id === record.entityId)?.name) ||
                      "-"}
                  </td>
                  <td>
                    <span
                      className={`${Styles.badge} ${Styles[record.entityType?.toLowerCase()]}`}
                    >
                      {record.entityType}
                    </span>
                  </td>
                  <td>{formatNocDate(record.nocExpiryAt)}</td>
                  <td>
                    <div className={Styles.actionBtns}>
                      <button
                        className={Styles.viewBtn}
                        title="View Document"
                        onClick={() => {
                          if (record.docId && onViewDoc) {
                            onViewDoc(record.docId);
                          }
                        }}
                        disabled={!record.docId || !onViewDoc}
                      >
                        <RemoveRedEyeIcon style={{ fontSize: "14px" }} />
                      </button>
                      <button
                        className={Styles.editBtn}
                        onClick={() => openEditModal(record)}
                      >
                        <BorderColorIcon style={{ fontSize: "14px" }} />
                      </button>
                      <button
                        className={Styles.deleteBtn}
                        onClick={() =>
                          setDeleteModal({ open: true, id: record.id })
                        }
                      >
                        <DeleteIcon style={{ fontSize: "14px" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Modal */}
      {formModal.open && (
        <Modal open={formModal.open} onClose={closeFormModal}>
          <div className={`popupModal ${Styles.formModal}`}>
            <div className={Styles.modalHeader}>
              <div className="headingText">
                {formModal.editData
                  ? "Edit Trader Entity"
                  : "Add Trader Entity"}
              </div>
            </div>
            <div className={Styles.formBody}>
              <SelectInput
                heading="Entity Type"
                list={entityTypeList}
                value={formData.entityType}
                onChange={(val) =>
                  handleFieldChange("entityType", val?.id || val)
                }
                example="Select entity type"
                height={40}
                error={errors.entityType}
              />

              <SelectInput
                heading={formData.entityType === "CONSUMER" ? "Select Consumer" : "Select Generator"}
                list={entityOptions}
                value={formData.selectedEntity?.id || ""}
                onChange={(val) => {
                  const selected = entityOptions.find(o => o.id === (val?.id || val));
                  handleFieldChange("selectedEntity", selected);
                }}
                example="Select company..."
                height={40}
                error={errors.selectedEntity}
              />

              <Fileupload
                heading="Document *"
                value={formData.docId}
                onChange={handleFileChange}
                field="docId"
                id="traderEntityDoc"
                accept=".pdf"
                height={45}
                example="Upload document (PDF)"
                error={errors.docId}
              />

              <MuiDateInput
                heading="NOC Expiry Date *"
                value={formData.nocExpiryAt}
                onChange={(date) => handleFieldChange("nocExpiryAt", date)}
                minDate={new Date()}
                height={40}
                error={errors.nocExpiryAt}
              />
            </div>
            <div className={Styles.formFooter}>
              <button
                className={Styles.cancelBtn}
                onClick={closeFormModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className={Styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : formModal.editData
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteModal.open && (
        <ConfirmationModal
          open={deleteModal.open}
          handleClose={() => setDeleteModal({ open: false, id: null })}
          header="Delete Trader Entity"
          data="Are you sure you want to delete this trader entity mapping?"
          handleSubmit={handleDelete}
          loading={deleting}
        />
      )}
    </div>
  );
};

export default TraderEntity;
